// Batch 28 · item 10 (M55): the search bar suggests every screen of the sidebar — built from the sidebar definition itself
// (Layout NAV_GROUPS, passed in), never from a separate hand list. Each sidebar page carries its EN and AR titles, its
// group's titles and a few short aliases ("KAM", "EQ", "W11"…). Matching is word-prefix: each typed word must start a word
// of the title, the group or an alias ("kam regis" → KAM register, "clear gate" → Clearance gate, "eq rev" → EQ review).
import { translations, t as tt } from "@/lib/i18n";

// short names auditors type (route → aliases); the titles themselves come from the sidebar
export const SCREEN_ALIASES = {
  "/completion/kam-register": ["KAM", "KAMs", "key audit matters"],
  "/completion/clearance-gate": ["clearance", "W11", "gate"],
  "/completion/eq-review": ["EQ", "EQR", "engagement quality", "522(a)"],
  "/completion/other-information": ["OI", "other info", "720"],
  "/completion/summary-memo": ["W10", "summary memo", "opinion"],
  "/completion/misstatements": ["W9", "misstatements", "SAD"],
  "/review-points": ["RP", "review notes", "review points"],
  "/ifrs": ["FS", "financial statements", "IFRS statements", "SFP", "SOPL"],
  "/close/file-assembly": ["W12", "file assembly", "archive", "lock"],
  "/fieldwork/a9001": ["A9001", "JE", "journal entry testing"],
  "/fieldwork/a9003": ["A9003", "opening balances"],
  "/fieldwork/adjusting-entries": ["AJE", "adjusting entries", "adjustments"],
  "/controls/a9002": ["A9002", "TOC", "tests of controls"],
  "/controls/203a": ["203(a)", "control design"],
  "/disclosure-checklist": ["DC", "disclosures"],
  "/reporting/auditors-report": ["report", "audit report", "auditor's report"],
  "/gl-import": ["GL", "TB", "general ledger", "trial balance", "import"],
  "/head-folders": ["HF", "lead sheets", "worksheets"],
  "/audit-response": ["W3", "response plan"],
  "/risk": ["risks", "RoMM"],
  "/scoping": ["scope"],
  "/doc-summaries": ["document summaries"],
  "/audit-trail": ["W13", "history", "log"],
};

const AR_DIACRITICS = /[ً-ٰٟـ]/g;
export function norm(s) {
  return String(s || "").toLowerCase().replace(AR_DIACRITICS, "").replace(/[إأآٱ]/g, "ا").replace(/ة/g, "ه").replace(/ى/g, "ي")
    .replace(/[⁦-⁩]/g, "");
}
const WORD = /[0-9a-z؀-ۿ]+(?:\([a-z]\))?/g;
export function words(s) { return norm(s).match(WORD) || []; }

/** 0 = no match; higher = better. Every typed word must start a word of one of the texts (taken together). */
export function screenScore(query, texts) {
  const qw = words(query);
  if (!qw.length) return 0;
  const all = texts.flatMap((x) => words(x));
  if (!qw.every((q) => all.some((w) => w.startsWith(q)))) return 0;
  const nq = norm(query).trim();
  const title = norm(texts[0] || "");
  if (title === nq) return 100;
  if (title.startsWith(nq)) return 90;
  const tw = words(texts[0] || "");
  if (qw.every((q) => tw.some((w) => w.startsWith(q)))) return 80 - Math.min(20, Math.max(0, tw.length - qw.length));
  return 60;
}

function label(item, dict, isReview) {
  const k = (isReview && item.reviewLabelKey) || item.labelKey || item.key;
  return k ? tt(dict, k) : (item.label || item.to);
}

/** The sidebar as a flat list: { to, title_en, title_ar, group_en, group_ar, aliases }. Groups are already filtered by the
 *  caller (roles, review-only), so a page the user cannot see in the sidebar is never suggested. */
export function screenEntries(groups, { isReview = false } = {}) {
  const out = [];
  const seen = new Set();
  for (const g of groups || []) {
    const gEn = g.labelKey ? tt(translations.en, g.labelKey) : (g.label || "");
    const gAr = g.labelKey ? tt(translations.ar, g.labelKey) : (g.label || "");
    for (const it of g.items || []) {
      if (!it.to || seen.has(it.to + (it.labelKey || it.key || ""))) continue;
      seen.add(it.to + (it.labelKey || it.key || ""));
      out.push({ to: it.to, title_en: label(it, translations.en, isReview), title_ar: label(it, translations.ar, isReview),
                 group_en: gEn, group_ar: gAr, aliases: SCREEN_ALIASES[it.to] || [], testid: it.testid || "" });
    }
  }
  return out;
}

/** Search hits in the shape the search bar renders (category Screens). */
export function searchScreens(query, entries, limit = 8) {
  const q = String(query || "").trim();
  if (q.length < 2) return [];
  const hits = [];
  for (const e of entries || []) {
    const s = Math.max(screenScore(q, [e.title_en, ...e.aliases, e.group_en]), screenScore(q, [e.title_ar, ...e.aliases, e.group_ar]));
    if (s) hits.push({ category: "Screens", title: e.title_en, title_ar: e.title_ar, subtitle: e.group_en, subtitle_ar: e.group_ar,
                       route: e.to, params: {}, score: s, local_screen: true });
  }
  return hits.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title)).slice(0, limit);
}
